/**
 * useEvidencias - Hook para gerenciar evidências (fotos/documentos) da API real
 *
 * Epic 2 - Story 2.1: Conexão com API /api/jobs/{jobId}/evidencias/
 * Substitui o useFotografias (MOCKUP) por dados reais do backend
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import apiClient from '../services/apiClient';
import { ClassificacaoDocumento } from './useFotografias';

// ==================== TIPOS ====================

export interface Evidencia {
  id: number;
  job_id: number;
  tipo: string;
  tipo_display: string;
  arquivo_url: string | null;
  arquivo_nome: string | null;
  descricao: string | null;
  produto_id: number | null;
  classificacao_id: number | null;
  classificacao_nome: string | null;
  classificacao_abrev: string | null;
  // Relacionamentos
  hospital_id: number | null;
  cliente_id: number | null;
  // GPS metadata
  gps_latitude: number | null;
  gps_longitude: number | null;
  gps_altitude: number | null;
  gps_accuracy: number | null;
  has_gps: boolean;
  gps_display: string;
  // File metadata
  file_size_bytes: number | null;
  file_size_display: string;
  file_width: number | null;
  file_height: number | null;
  // Device info
  device_model: string | null;
  os_version: string | null;
  app_version: string | null;
  // User info
  captured_by_id: number | null;
  captured_by_name: string | null;
  // Timestamps
  captured_at: string | null;
  synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface EvidenciaListResponse {
  items: Evidencia[];
  total: number;
  limit: number;
  offset: number;
}

export interface EvidenciaStats {
  job_id: number;
  total: number;
  por_tipo: {
    FOTO_PRODUTO: number;
    FOTO_LOCAL: number;
    DOCUMENTO: number;
    OUTROS: number;
  };
  gps: {
    com_gps: number;
    sem_gps: number;
    percentual_com_gps: number;
  };
  sync: {
    sincronizadas: number;
    pendentes: number;
  };
  por_cliente: Record<string, number>;
}

export interface FilterOption {
  value: string | number;
  label: string;
}

// ==================== OPÇÕES DE FILTRO ESTÁTICAS ====================

export const TIPO_OPTIONS: FilterOption[] = [
  { value: 'FOTO_PRODUTO', label: 'Foto Produto' },
  { value: 'FOTO_LOCAL', label: 'Foto Local' },
  { value: 'DOCUMENTO', label: 'Documento' },
  { value: 'OUTROS', label: 'Outros' },
];

export const GPS_OPTIONS: FilterOption[] = [
  { value: 'true', label: 'Com GPS' },
  { value: 'false', label: 'Sem GPS' },
];

// ==================== HOOK ====================

interface UseEvidenciasOptions {
  jobId: number;
  globalClienteFilter?: (string | number)[];
  autoLoad?: boolean;
}

export function useEvidencias({
  jobId,
  globalClienteFilter,
  autoLoad = true,
}: UseEvidenciasOptions) {
  // Estado de dados
  const [evidencias, setEvidencias] = useState<Evidencia[]>([]);
  const [stats, setStats] = useState<EvidenciaStats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [classificacoes, setClassificacoes] = useState<ClassificacaoDocumento[]>([]);

  // Estado de paginação
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Estado de lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Estados de filtro
  const [selectedTipo, setSelectedTipo] = useState<(string | number)[]>([]);
  const [selectedGps, setSelectedGps] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Usa filtro global se fornecido
  const effectiveClienteFilter = globalClienteFilter ?? [];

  // ==================== FETCH FUNCTIONS ====================

  const fetchEvidencias = useCallback(async () => {
    if (!jobId) return;

    setLoading(true);
    setError(null);

    try {
      // Construir query params
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      // Filtrar por tipo (pega primeiro se houver múltiplos)
      if (selectedTipo.length > 0) {
        params.append('tipo', selectedTipo[0].toString());
      }

      // Filtrar por cliente
      if (effectiveClienteFilter.length > 0) {
        params.append('cliente_id', effectiveClienteFilter[0].toString());
      }

      // Filtrar por GPS
      if (selectedGps !== null) {
        params.append('has_gps', selectedGps);
      }

      const response = await apiClient.get<EvidenciaListResponse>(
        `/jobs/${jobId}/evidencias/?${params.toString()}`
      );

      setEvidencias(response.data.items);
      setTotal(response.data.total);
    } catch (err: unknown) {
      console.error('Erro ao carregar evidências:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar evidências');
      setEvidencias([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [jobId, limit, offset, selectedTipo, effectiveClienteFilter, selectedGps]);

  const fetchStats = useCallback(async () => {
    if (!jobId) return;

    try {
      const response = await apiClient.get<EvidenciaStats>(
        `/jobs/${jobId}/evidencias/stats/`
      );
      setStats(response.data);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  }, [jobId]);

  const fetchClassificacoes = useCallback(async () => {
    try {
      const response = await apiClient.get<ClassificacaoDocumento[]>(
        '/config/classificacao-documento'
      );
      setClassificacoes(response.data);
    } catch (err) {
      console.error('Erro ao carregar classificações:', err);
    }
  }, []);

  // Auto-load ao montar e quando filtros mudam
  useEffect(() => {
    if (autoLoad) {
      fetchEvidencias();
    }
  }, [autoLoad, fetchEvidencias]);

  useEffect(() => {
    if (autoLoad) {
      fetchStats();
    }
  }, [autoLoad, fetchStats]);

  useEffect(() => {
    if (autoLoad) {
      fetchClassificacoes();
    }
  }, [autoLoad, fetchClassificacoes]);

  // ==================== FILTRO LOCAL (SEARCH) ====================

  const filteredEvidencias = useMemo(() => {
    if (!searchTerm.trim()) {
      return evidencias;
    }

    const term = searchTerm.toLowerCase().trim();
    return evidencias.filter(
      (e) =>
        e.arquivo_nome?.toLowerCase().includes(term) ||
        e.descricao?.toLowerCase().includes(term) ||
        e.tipo_display?.toLowerCase().includes(term) ||
        e.captured_by_name?.toLowerCase().includes(term)
    );
  }, [evidencias, searchTerm]);

  // ==================== CONTADORES ====================

  const counts = useMemo(() => {
    const images = filteredEvidencias.filter(
      (e) => e.tipo === 'FOTO_PRODUTO' || e.tipo === 'FOTO_LOCAL'
    ).length;
    const pdfs = filteredEvidencias.filter((e) => e.tipo === 'DOCUMENTO').length;
    const outros = filteredEvidencias.filter((e) => e.tipo === 'OUTROS').length;
    const comGps = filteredEvidencias.filter((e) => e.has_gps).length;
    const semGps = filteredEvidencias.length - comGps;

    return {
      images,
      pdfs,
      outros,
      total: filteredEvidencias.length,
      comGps,
      semGps,
    };
  }, [filteredEvidencias]);

  // ==================== HELPERS ====================

  const hasActiveFilters = useMemo(() => {
    return (
      selectedTipo.length > 0 || selectedGps !== null || searchTerm.trim() !== ''
    );
  }, [selectedTipo, selectedGps, searchTerm]);

  const clearAllFilters = useCallback(() => {
    setSelectedTipo([]);
    setSelectedGps(null);
    setSearchTerm('');
    setOffset(0);
  }, []);

  // ==================== LIGHTBOX HANDLERS ====================

  const openLightbox = useCallback((index: number) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentPhotoIndex((prev) =>
      prev > 0 ? prev - 1 : filteredEvidencias.length - 1
    );
  }, [filteredEvidencias.length]);

  const goToNext = useCallback(() => {
    setCurrentPhotoIndex((prev) =>
      prev < filteredEvidencias.length - 1 ? prev + 1 : 0
    );
  }, [filteredEvidencias.length]);

  // ==================== PAGINAÇÃO ====================

  const nextPage = useCallback(() => {
    if (offset + limit < total) {
      setOffset((prev) => prev + limit);
    }
  }, [offset, limit, total]);

  const prevPage = useCallback(() => {
    if (offset > 0) {
      setOffset((prev) => Math.max(0, prev - limit));
    }
  }, [offset, limit]);

  const goToPage = useCallback(
    (page: number) => {
      setOffset(page * limit);
    },
    [limit]
  );

  // ==================== UPDATE (PATCH) ====================

  const updateEvidencia = useCallback(
    async (evidenciaId: number, updates: { descricao?: string; classificacao_id?: number }) => {
      if (!jobId) return false;

      try {
        // Send as JSON body (matches Flutter format, backend accepts both)
        await apiClient.patch(`/jobs/${jobId}/evidencias/${evidenciaId}/`, updates);
        // Recarregar lista após update
        await fetchEvidencias();
        return true;
      } catch (err) {
        console.error('Erro ao atualizar evidência:', err);
        return false;
      }
    },
    [jobId, fetchEvidencias]
  );

  // ==================== DELETE ====================

  const deleteEvidencia = useCallback(
    async (evidenciaId: number) => {
      if (!jobId) return false;

      try {
        await apiClient.delete(`/jobs/${jobId}/evidencias/${evidenciaId}/`);
        // Recarregar lista após delete
        await fetchEvidencias();
        await fetchStats();
        return true;
      } catch (err) {
        console.error('Erro ao deletar evidência:', err);
        return false;
      }
    },
    [jobId, fetchEvidencias, fetchStats]
  );

  // ==================== RETURN ====================

  return {
    // Estado de dados
    evidencias: filteredEvidencias,
    allEvidencias: evidencias,
    stats,
    total,
    loading,
    error,

    // Paginação
    limit,
    offset,
    setLimit,
    nextPage,
    prevPage,
    goToPage,
    currentPage: Math.floor(offset / limit),
    totalPages: Math.ceil(total / limit),

    // Lightbox
    lightboxOpen,
    currentPhotoIndex,
    openLightbox,
    closeLightbox,
    goToPrevious,
    goToNext,
    setCurrentPhotoIndex,

    // Filtros
    selectedTipo,
    selectedGps,
    searchTerm,
    setSelectedTipo,
    setSelectedGps,
    setSearchTerm,
    hasActiveFilters,
    clearAllFilters,

    // Opções de filtro
    tipoOptions: TIPO_OPTIONS,
    gpsOptions: GPS_OPTIONS,

    // Contadores
    counts,

    // Classificações reais do backend
    classificacoes,

    // Ações
    refresh: fetchEvidencias,
    refreshStats: fetchStats,
    updateEvidencia,
    deleteEvidencia,
  };
}

export default useEvidencias;
